import React, { useState, useEffect } from 'react';
import API_BASE_URL from '../../../components/apiconfig/api-config';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const HighSeasAgreementForm = () => {
  const [formData, setFormData] = useState({
    agreement_date: '',
    kribhco_id: '',
    supplier_id: '',
    commodity_id: '',
    crop_year: '',
    order_id: '',
    supplier_invoice_ref_no: '',
    hss_usd_pmt: '',
  });

  const [loading, setLoading] = useState(false);
  // const [message, setMessage] = useState(null); // No longer needed for errors, only for success if you want
  const [suppliers, setSuppliers] = useState([]);
  const [kribhcoList, setKribhcoList] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [orders, setOrders] = useState([]);
  const navigate = useNavigate();

  const FIELD_LIMITS = {
    agreement_date: 10, // yyyy-mm-dd
    crop_year: 20,
    supplier_invoice_ref_no: 100,
  };

  const validateField = (name, value) => {
    let error = '';
    const limit = FIELD_LIMITS[name];
    if (limit && value && value.length > limit) {
      error = `Maximum ${limit} characters allowed for ${name.replace('_', ' ')}`;
    }
    // Required fields
    const requiredFields = [
      'agreement_date', 'kribhco_id', 'supplier_id', 'commodity_id',
      'order_id', 'hss_usd_pmt'
    ];
    if (requiredFields.includes(name) && (!value || value === '' || value === 0)) {
      error = `${name.replace('_', ' ')} is required`;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      toast.error(error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch suppliers
        const supplierRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-suppliers`, {
          credentials: 'include'
        });
        if (!supplierRes.ok) throw new Error('Failed to fetch suppliers');
        const supplierData = await supplierRes.json();
        setSuppliers(supplierData.data || []);

        // Fetch kribhco
        const kribhcoRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-kribhco`, {
          credentials: 'include'
        });
        if (!kribhcoRes.ok) throw new Error('Failed to fetch kribhco');
        const kribhcoData = await kribhcoRes.json();
        setKribhcoList(kribhcoData.data || []);

        // Fetch commodities
        const commodityRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-commodities`, {
          credentials: 'include'
        });
        if (!commodityRes.ok) throw new Error('Failed to fetch commodities');
        const commodityData = await commodityRes.json();
        setCommodities(commodityData.data || []);

        // Fetch orders
        const orderRes = await fetch(`${API_BASE_URL}/api/masters/dropdown-import-order`, {
          credentials: 'include'
        });
        if (!orderRes.ok) throw new Error('Failed to fetch orders');
        const orderData = await orderRes.json();
        setOrders(orderData.data || []);
      } catch (err) {
        console.error('Data fetch error:', err);
        toast.error('Failed to load dropdown data');
      }
    };

    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleDropdownChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    // setMessage(null);
    // Validation: check all fields for max length
    for (const key in formData) {
      if (FIELD_LIMITS[key] && formData[key] && formData[key].length > FIELD_LIMITS[key]) {
        toast.error(`Maximum ${FIELD_LIMITS[key]} characters allowed for ${key.replace('_', ' ')}`);
        setLoading(false);
        return;
      }
    }
    // Validation: check required fields
    const requiredFields = [
      'agreement_date', 'kribhco_id', 'supplier_id', 'commodity_id',
      'order_id', 'hss_usd_pmt'
    ];
    for (const field of requiredFields) {
      if (!formData[field] || formData[field] === '' || formData[field] === 0) {
        toast.error(`${field.replace('_', ' ')} is required`);
        setLoading(false);
        return;
      }
    }
    
    // Prepare the data with proper formatting
    const submissionData = {
      ...formData,
      kribhco_id: Number(formData.kribhco_id),
      supplier_id: Number(formData.supplier_id),
      commodity_id: Number(formData.commodity_id),
      order_id: Number(formData.order_id),
      hss_usd_pmt: Number(formData.hss_usd_pmt),
      supplier_invoice_ref_no: formData.supplier_invoice_ref_no || null,
      crop_year: formData.crop_year || null
    };

    try {
      const res = await fetch(`${API_BASE_URL}/api/agreement/high-seas-agreements-post`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(submissionData),
      });

      // Check response content type
      const contentType = res.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await res.text();
        throw new Error(`Server returned unexpected response: ${text.substring(0, 100)}`);
      }

      const data = await res.json();
      
      if (!res.ok || !data.success) {
        throw new Error(data.error || data.message || 'Request failed');
      }

      toast.success(data.message || 'High Seas Agreement Created Successfully!');
      
      // Reset form
      setFormData({
        agreement_date: '',
        kribhco_id: '',
        supplier_id: '',
        commodity_id: '',
        crop_year: '',
        order_id: '',
        supplier_invoice_ref_no: '',
        hss_usd_pmt: '',
      });

    } catch (err) {
      console.error('Submission Error:', err);
      toast.error(err.message.includes('Unexpected token') 
        ? 'Server returned an invalid response. Please check the API endpoint.' 
        : err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-8xl mx-auto p-0 bg-white">
      <div className="flex justify-between items-center border-b border-gray-100 bg-gray-50 px-4 py-1.5">
        <h2 className="text-lg font-bold mb-0 text-left">High Seas Agreement Entry</h2>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-[#10a5e9] border border-[#10a5e9] px-2 py-0.5 rounded text-xs font-medium hover:bg-[#eaf6fd]"
        >
          <FaArrowLeft />
          Back
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-0">
        <div className="px-4 pt-4 pb-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium mb-1">Agreement Date</label>
              <input 
                type="date" 
                name="agreement_date" 
                value={formData.agreement_date} 
                onChange={handleChange} 
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                required 
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Kribhco</label>
              <SearchableDropdown
                options={kribhcoList}
                value={formData.kribhco_id}
                onChange={(value) => handleDropdownChange('kribhco_id', value)}
                placeholder="Select Kribhco"
                displayKey="business_name"
                valueKey="id"
                required
                onBlur={handleBlur}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Supplier</label>
              <SearchableDropdown
                options={suppliers}
                value={formData.supplier_id}
                onChange={(value) => handleDropdownChange('supplier_id', value)}
                placeholder="Select Supplier"
                displayKey="name"
                valueKey="supplier_id"
                required
                onBlur={handleBlur}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Commodity</label>
              <SearchableDropdown
                options={commodities}
                value={formData.commodity_id}
                onChange={(value) => handleDropdownChange('commodity_id', value)}
                placeholder="Select Commodity"
                displayKey="name"
                valueKey="commodity_id"
                required
                onBlur={handleBlur}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Crop Year</label>
              <input 
                type="text" 
                name="crop_year" 
                value={formData.crop_year} 
                onChange={handleChange} 
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.crop_year}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                placeholder="e.g., 2024"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Order</label>
              <SearchableDropdown
                options={orders}
                value={formData.order_id}
                onChange={(value) => handleDropdownChange('order_id', value)}
                placeholder="Select Order"
                displayKey="order_id"
                valueKey="order_id"
                required
                onBlur={handleBlur}
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">Supplier Invoice Reference No</label>
              <input 
                type="text" 
                name="supplier_invoice_ref_no" 
                value={formData.supplier_invoice_ref_no} 
                onChange={handleChange} 
                onBlur={handleBlur}
                maxLength={FIELD_LIMITS.supplier_invoice_ref_no}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">HSS USD PMT</label>
              <input 
                type="number" 
                name="hss_usd_pmt" 
                value={formData.hss_usd_pmt} 
                onChange={handleChange} 
                step="0.01"
                onBlur={handleBlur}
                className="w-full border border-gray-300 px-3 py-1.5 text-sm bg-white focus:outline-none focus:border-[#10a5e9] rounded-none" 
                required 
              />
            </div>
          </div>
        </div>

        <div className="px-4 pt-2 pb-4">
          <button
            type="submit"
            className="w-auto px-4 py-1.5 text-sm bg-[#10a5e9] text-white font-semibold tracking-wide rounded border border-[#10a5e9] hover:bg-[#0e8ac2] transition disabled:opacity-60"
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save High Seas Agreement'}
          </button>
          <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick pauseOnFocusLoss draggable pauseOnHover />
        </div>
      </form>
    </div>
  );
};

export default HighSeasAgreementForm;